import React from "react";
import { toast } from "react-toastify";
import { RiResetLeftFill } from "react-icons/ri";

const CompanyForm = ({ 
    companyData, 
    setCompanyData, 
    handleChange, 
    handleSubmit, 
    isEditable, 
    handleEdit, 
    resetForm ,
    errors,setErrors
}) => {


    // Generate Secret Key
    const generateSecretKey = () => {
        return Math.random().toString().slice(2, 8);
    };

    // Generate Chat URL
    const handleGeneratechat_Url = () => {
        if (!companyData.companyName.trim()) {
            toast.error("Company Name is required to generate URL");
            setErrors(prev => ({ ...prev, companyName: "Company Name is required!" }));
            return;
        }
        setErrors(prev => ({ ...prev, chat_Url: "" }));
        const secretKey = generateSecretKey();
        setCompanyData(prev => ({
            ...prev,
            secretKey: secretKey,
            chat_Url: `talk/${encodeURIComponent(prev.companyName)}`
        }));
    };

    // Remove Chat URL
    const handleRemovechat_Url = () => {
        setCompanyData(prev => ({
            ...prev,
            chat_Url: "",
            secretKey: ""
        }));
    };

    // Form Validation
    const validateForm = () => {
        let newErrors = {};
        if (!companyData.companyName.trim()) newErrors.companyName = "Company Name is required!";
        if (!companyData.companyType.trim()) newErrors.companyType = "Company Type is required!";
        const urlPattern = /^(https?:\/\/[^\s]+)/;
        if (!companyData.companyLinks.trim()) {
            newErrors.companyLinks = "Company URL is required!";
        } else if (!urlPattern.test(companyData.companyLinks)) {
            newErrors.companyLinks = "Please enter a valid URL (e.g., https://www.example.com)";
        }
        if (!companyData.companyLocation.trim()) newErrors.companyLocation = "Location is required!";
        if (!companyData.companyAbout.trim()) newErrors.companyAbout = "About Company is required!";
        if (!companyData.companyProducts.trim()) newErrors.companyProducts = "Company Products are required!";
        if (!companyData.companyServices.trim()) newErrors.companyServices = "Company Services are required!";
        if (!companyData.chat_Url) newErrors.chat_Url = "Generate Chat URL before submitting!";
        if (!companyData.logo) newErrors.logo = "Logo is required!";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle Submit
    const onSubmit = () => {
        if (validateForm()) {
            isEditable ? handleEdit() : handleSubmit();
        }
    };

    return (
        <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        className="bg-white shadow-lg rounded-lg p-6 sm:p-8 w-full max-w-4xl mx-auto"
      >
        {/* Header: stack vertically on small screens, horizontal on md */}
        <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4 md:gap-0">
          <h2 className="text-2xl md:text-3xl font-bold flex-1 text-center md:text-left">
            {isEditable ? "Edit Company Details" : "Add Company Details"}
          </h2>
          <button
            type="button"
            onClick={resetForm}
            className="bg-red-500 text-white px-5 py-2 rounded-md flex items-center gap-2 hover:bg-red-600 transition w-full md:w-auto justify-center"
          >
            <RiResetLeftFill className="text-xl" />
            Reset
          </button>
        </div>
      
        {/* Form Grid: 1 col on mobile, 2 cols on md+ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Logo Upload + Preview */}
          <div className="flex flex-col">
            <label className="block text-gray-700 font-semibold mb-2">Company Logo</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setCompanyData((prev) => ({
                    ...prev,
                    logo: file,
                  }));
                }
              }}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-300"
            />
            {errors.logo && <p className="text-red-500 text-sm mt-1">{errors.logo}</p>}
      
            {/* Center preview on mobile */}
            {companyData.logo && (
              <div className="mt-4 flex justify-center md:justify-start">
                <img
                  src={
                    companyData.logo instanceof File
                      ? URL.createObjectURL(companyData.logo)
                      : companyData.logo
                  }
                  alt="Company Logo"
                  className="w-32 h-auto object-contain border rounded"
                />
              </div>
            )}
          </div>
      
          {/* Other inputs stacked with space */}
          <div className="space-y-6">
            {[
              { label: "Company Name", name: "companyName", placeholder: "Enter company name" },
              { label: "Company Type", name: "companyType", placeholder: "Enter company type" },
              { label: "Company URL", name: "companyLinks", placeholder: "Enter company URL" },
              { label: "Location", name: "companyLocation", placeholder: "Enter company location" },
            ].map(({ label, name, placeholder }) => (
              <div key={name}>
                <label className="block text-gray-700 font-semibold mb-2">{label}</label>
                <input
                  type="text"
                  name={name}
                  value={companyData[name]}
                  onChange={handleChange}
                  placeholder={placeholder}
                  className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring ${
                    errors[name] ? "border-red-500" : "focus:ring-blue-300"
                  }`}
                />
                {errors[name] && <p className="text-red-500 text-sm mt-1">{errors[name]}</p>}
              </div>
            ))}
          </div>
        </div>
      
        {/* Textareas: stack 1 col on mobile, 3 cols md+ */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          {["companyAbout", "companyProducts", "companyServices"].map((field) => (
            <div key={field} className="flex flex-col">
              <label className="block text-gray-700 font-semibold mb-2">
                {field
                  .replace("company", "")
                  .replace(/([A-Z])/g, " $1")
                  .trim()}
              </label>
              <textarea
                name={field}
                value={companyData[field]}
                onChange={handleChange}
                placeholder={`Describe your ${field.replace("company", "").toLowerCase()}...`}
                rows="4"
                className={`w-full px-4 py-3 border rounded-md resize-none focus:outline-none focus:ring ${
                  errors[field] ? "border-red-500" : "focus:ring-blue-300"
                }`}
              />
              {errors[field] && <p className="text-red-500 text-sm mt-1">{errors[field]}</p>}
            </div>
          ))}
        </div>
      
        {/* Chat URL Section */}
        <div className="mt-8">
          {!companyData.chat_Url && (
            <button
              type="button"
              onClick={handleGeneratechat_Url}
              className="w-full bg-green-500 text-white py-3 rounded-md hover:bg-green-600 transition duration-300"
            >
              Generate Chat URL
            </button>
          )}
      
          {errors.chat_Url && <p className="text-red-500 text-sm mt-2">{errors.chat_Url}</p>}
      
          {companyData.chat_Url && (
            <div className="mt-4 bg-gray-100 p-4 rounded-md flex flex-col md:flex-row items-center justify-between gap-4">
              <span className="font-semibold">Link: </span>
              <a
                href={companyData.chat_Url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline break-words max-w-full"
              >
                {companyData.chat_Url}
              </a>
              <span>
                <span className="font-semibold">Secret Key: </span>
                {companyData.secretKey}
              </span>
              <button
                onClick={handleRemovechat_Url}
                className="ml-auto text-red-500 hover:text-red-700"
                aria-label="Remove chat URL"
              >
                ✕
              </button>
            </div>
          )}
        </div>
      
        {/* Submit Button */}
        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700 transition duration-300 mt-10"
        >
          {isEditable ? "Save" : "Add"}
        </button>
      </form>
      
    );
};

export default CompanyForm;
